/**
 * Client de l'API du portail.
 *
 * Toutes les requetes passent par `request`, qui porte le jeton et
 * traduit les erreurs HTTP en quelque chose d'actionnable a l'ecran.
 */
import { session } from "./session";

export type ApiErrorKind =
  | "unauthorized" // 401 - jeton absent, expire, ou identifiants faux
  | "mfa_required" // 401/403 avec un detail parlant de MFA
  | "forbidden" // 403 - le role ne permet pas cette action
  | "not_found"
  | "conflict"
  | "validation" // 422 - le serveur a refuse la saisie
  | "rate_limited"
  | "network"
  | "server";

/** Emis quand le serveur refuse le jeton : retour a la connexion. */
export const SESSION_EXPIREE = "sign365:session-expiree";

export class ApiError extends Error {
  readonly kind: ApiErrorKind;
  readonly status: number;
  readonly detail: string | null;
  /** Le corps JSON de la reponse, quand il porte plus qu'un message (liste
   *  des comptes a la connexion, 08/10/2026). */
  readonly corps: Record<string, unknown> | null;

  constructor(kind: ApiErrorKind, status: number, detail: string | null, corps: Record<string, unknown> | null = null) {
    super(detail ?? kind);
    this.kind = kind;
    this.status = status;
    this.detail = detail;
    this.corps = corps;
  }
}

/** Le backend refuse la connexion d'un role sensible sans MFA. Ce n'est
 *  PAS "identifiants invalides" : afficher ce message-la est la premiere
 *  source de confusion a la premiere connexion (CDC §4.1). */
function estMfa(detail: string | null): boolean {
  return Boolean(detail && /mfa|totp|double facteur|second facteur/i.test(detail));
}

function kindFor(status: number, detail: string | null): ApiErrorKind {
  if (estMfa(detail)) return "mfa_required";
  switch (status) {
    case 401:
      return "unauthorized";
    case 403:
      return "forbidden";
    case 404:
      return "not_found";
    case 409:
      return "conflict";
    case 422:
      return "validation";
    case 429:
      return "rate_limited";
    default:
      return status >= 500 ? "server" : "validation";
  }
}

interface Options {
  method?: string;
  body?: unknown;
  /** Requete non authentifiee (connexion, mot de passe oublie). */
  anonyme?: boolean;
  /** Reponse binaire (PDF, zip) plutot que JSON. */
  binaire?: boolean;
}

async function request<T>(chemin: string, options: Options = {}): Promise<T> {
  const entetes: Record<string, string> = { Accept: "application/json" };
  if (!options.anonyme) {
    const token = session.token();
    if (token) entetes.Authorization = `Bearer ${token}`;
  }

  let init: RequestInit = { method: options.method ?? "GET", headers: entetes };
  if (options.body instanceof FormData) {
    // Surtout PAS de Content-Type ici : le navigateur doit poser lui-meme
    // la frontiere multipart, sinon le serveur ne sait pas la lire.
    init = { ...init, body: options.body };
  } else if (options.body !== undefined) {
    entetes["Content-Type"] = "application/json";
    init = { ...init, body: JSON.stringify(options.body) };
  }

  let reponse: Response;
  try {
    reponse = await fetch(`/api${chemin}`, init);
  } catch {
    throw new ApiError("network", 0, null);
  }

  if (!reponse.ok) {
    let detail: string | null = null;
    let corps: Record<string, unknown> | null = null;
    try {
      corps = await reponse.json();
      detail = typeof corps?.detail === "string" ? corps.detail : null;
    } catch {
      /* corps non-JSON : le statut suffit */
    }
    const kind = kindFor(reponse.status, detail);
    // Jeton expire en cours de navigation : on ferme la session pour que
    // l'ecran de connexion reprenne la main, plutot que de laisser
    // l'utilisateur face a des erreurs incomprehensibles a chaque clic.
    if (kind === "unauthorized" && !options.anonyme) {
      session.fermer();
      // Fermer le stockage ne suffisait pas : l'ecran restait affiche avec
      // « Votre session a expire » (porteur, 06/10/2026). L'application
      // ecoute cet evenement et revient a la page de connexion.
      window.dispatchEvent(new Event(SESSION_EXPIREE));
    }
    throw new ApiError(kind, reponse.status, detail, corps);
  }

  if (options.binaire) return (await reponse.blob()) as unknown as T;
  if (reponse.status === 204) return undefined as T;
  return (await reponse.json()) as T;
}

// ------------------------------------------------------------- types

/** Fiche client (08/10/2026) : particulier ou societe. */
export interface FicheClient {
  type: "particulier" | "societe";
  prenom: string | null;
  nom: string | null;
  raison_sociale: string | null;
  siren: string | null;
  tva: string | null;
  adresse: string | null;
  code_postal: string | null;
  ville: string | null;
  pays: string;
  tel_mobile: string | null;
  tel_fixe: string | null;
  email_facturation: string | null;
  complete: boolean;
}

export interface Moi {
  user_id: string;
  email: string;
  role: string;
  tenant_id: string;
  /** Le compte : son nom et son identifiant (« numero de compte », celui
   *  que le backoffice recherche). */
  tenant?: { id: string; name: string | null; slug: string | null; numero?: number | null } | null;
  /** 08/10/2026 : double authentification facultative pour les clients. */
  mfa_enabled?: boolean;
  /** Lot 2ter (19/10/2026) : prénom et nom de la personne connectée ;
   *  nom_verifie_le posé, ils viennent de son identité vérifiée et ne se
   *  modifient plus. */
  given_name?: string | null;
  family_name?: string | null;
  nom_verifie_le?: string | null;
  /** Les comptes dont l'utilisateur est membre (changer de compte). */
  comptes?: { numero: number; nom: string; role: string }[];
  /** Proprietaire d'un compte client ; sinon « Creer mon compte ». */
  proprietaire?: boolean;
}

/** Signataires par enveloppe, comme le serveur (NV-13, 05/10/2026). */
export const MAX_SIGNATAIRES = 50;

export interface Devis {
  prix_offre_minor: number;
  signataires_compris: number | null;
  signataires_en_plus: number;
  supplement_signataire_minor: number | null;
  supplement_minor: number;
  total_minor: number;
  currency_code: string;
}

export type ModeScellement = "sans" | "adobe" | "eidas";

/** File d'attente (08/10/2026) : la demande a son numero des l'envoi. */
export type EtatScellement = "en_file" | "scelle" | "echec";

export interface Scellement {
  id: string;
  etat: EtatScellement;
  erreur: string | null;
  mode: ModeScellement;
  horodateur: string | null;
  amount_minor: number;
  currency_code: string;
  created_at: string;
  scelle_le: string | null;
  title: string;
  filename: string | null;
  /** Dossier de preuve constitue (08/10/2026). */
  dossier: boolean;
}

export type StatutAdresse = "ACCEPTEE_SMTP" | "REJET_DEFINITIF" | "INDETERMINE" | "CATCH_ALL" | "BOITE_PLEINE"
  | "REFUS_EXPEDITEUR" | "EN_REPRISE";

/** Domaine d'expediteur du client (lot 3) : statut Scaleway, « checked » = verifie. */
export interface DomaineExpediteur {
  id: string;
  nom: string;
  statut: string;
  derniere_erreur: string | null;
  dns: { type: string; nom: string; valeur: string; note: string }[];
  verifie_le: string | null;
}

export interface Expediteurs {
  domaines: DomaineExpediteur[];
  adresses: { id: string; adresse: string; domaine_id: string; utilisable: boolean }[];
}

/** Modele de message de « Prouver » (decision J du 06/10/2026). */
export interface ModeleMessage {
  id: string;
  nom: string;
  objet: string;
  corps: string;
}

/** Deux offres (08/10/2026) : Email Adobe et Email eIDAS. */
export type ModePreuve = "adobe" | "eidas";

export interface PreuveEnvoi {
  id: string;
  mode: ModePreuve;
  destinataire: string;
  objet: string;
  statut: StatutAdresse | null;
  etat: "en_attente" | "constitue";
  demande_le: string;
  envoye_le: string | null;
  constitue_le: string | null;
  amount_minor: number;
  currency_code: string;
  created_at: string;
  /** Lot 5 : echeance de conservation, reculee a chaque prolongation. */
  peremption_le: string;
  prolongations: number;
}

export interface ConservationProuver {
  conservation_mois: number;
  prix_prolongation_minor: number;
  prolongation_auto: boolean;
}

export interface Produit {
  id: number;
  code: string;
  libelle: string;
  /** Operations facturees par cette offre. Le prix de l'offre est la
   *  SOMME de leurs tarifs : /pricing/resolve tarifie une operation, pas
   *  un produit. */
  operations?: string[];
}

export interface Solde {
  balance_minor: number;
  currency_code: string;
}

export interface EnveloppeResume {
  id: string;
  title: string;
  status: string;
  created_at: string;
  completed_at?: string | null;
  signer_count?: number | null;
  signed_count?: number | null;
  /** Nombre de signataires ayant signe (liste, GET /envelopes). */
  signer_signed?: number | null;
  /** Echeance de l'enveloppe (porteur, 12/10/2026 : a afficher dans la liste). */
  expires_at?: string | null;
  /** B-08 lot 2 : resultat de l'enveloppe (vide tant qu'elle est en cours) et son motif. */
  outcome?: "success" | "failure" | null;
  outcome_reason?: string | null;
}

export interface Signataire {
  id: string;
  email: string;
  full_name?: string | null;
  role?: string | null;
  status: string;
  auth_method?: string | null;
  viewed_at?: string | null;
  consent_recorded_at?: string | null;
  authenticated_at?: string | null;
  signed_at?: string | null;
  declined_at?: string | null;
  decline_reason?: string | null;
  /** B-07 : null = pas encore invite (ordre impose, son tour n'est pas venu). */
  invited_at?: string | null;
}

/** B-07 : un signataire invite, tel que renvoye a l'envoi ou a la relance.
 *  Le lien n'est a montrer que si l'e-mail n'est pas parti. */
export interface Invitation {
  signer_id: string;
  email: string;
  link: string;
  email_sent: boolean;
}

/** Traitement avant signature (porteur, 12/10/2026). */
export interface Intervenant {
  id: string;
  role: "remplit" | "valide";
  email: string;
  full_name?: string | null;
  statut: "attente" | "invite" | "termine" | "refuse";
  invite_le?: string | null;
  termine_le?: string | null;
  motif?: string | null;
}

export interface IntervenantEnvoye {
  email: string;
  full_name?: string | null;
  role: "remplit" | "valide";
  champs: ChampEnvoye[];
}

/** « Dupliquer » : tout ce qu'il faut pour pre-remplir la creation. */
export interface Duplication {
  title: string;
  product_id: number;
  langue?: string | null;
  signing_order_imposed: boolean;
  message?: string | null;
  copy_emails: string[];
  remplissage_ordre_impose: boolean;
  documents: { document_id: string; document_version_id: string; filename: string }[];
  signers: { email: string; full_name?: string | null; role: string; auth_method: "otp_email" | "otp_sms";
             phone_e164?: string | null; message?: string | null; langue?: string | null;
             transmission?: Reglage; correction_nom?: Reglage; champs: ChampEnvoye[] }[];
  intervenants: IntervenantEnvoye[];
}

/** Ce qui attend l'utilisateur connecte dans « À traiter ». */
export interface TacheATraiter {
  intervenant_id: string;
  role: "remplit" | "valide";
  envelope_id: string;
  title: string;
  client: string;
  invite_le?: string | null;
}

export interface ChampATraiter extends ChampEnvoye {
  id: string;
  valeur: string | null;
  rempli_par?: string | null;
  rempli_par_nom?: string | null;
}

export interface Traitement {
  intervenant_id: string;
  role: "remplit" | "valide";
  envelope_id: string;
  title: string;
  client: string;
  champs: ChampATraiter[];
}

/** Lot 2bis (15/10/2026) : autoriser / demander / interdire. */
export type Reglage = "autoriser" | "demander" | "interdire";

/** Lot 2bis : une demande d'un signataire (transmission, correction du nom). */
export interface DemandeSignataire {
  id: string;
  signer_id?: string;
  envelope_id?: string;
  title?: string;
  type: "transmission" | "nom";
  statut?: "en_attente" | "acceptee" | "refusee";
  /** La demande en une phrase (serveur). */
  resume: string;
  demandee_par: string;
  demandee_le: string;
  decidee_le?: string | null;
  decidee_par?: string | null;
}

export interface EnveloppeDetail extends EnveloppeResume {
  product_id?: number | null;
  /** Lot 2bis : demandes des signataires, la plus recente d'abord. */
  demandes?: DemandeSignataire[];
  /** Traitement avant signature : remplisseurs et valideur. */
  intervenants?: Intervenant[];
  /** Le document de l'enveloppe (sa version courante : signee a la fin). */
  document_id?: string | null;
  signing_order_imposed?: boolean;
  /** Adresses qui recoivent le lien du document signe (client, comptable...). */
  copy_emails?: string[];
  langue?: string | null;
  expires_at?: string | null;
  signers: Signataire[];
  /** Champs techniques exposes DELIBEREMENT : ils existent pour etre
   *  montres au client, qui doit voir OU ca bloque (CDC §4.4). */
  hsm_status?: string | null;
  tsa_status?: string | null;
  hsm_attempt_count?: number | null;
  tsa_attempt_count?: number | null;
  hsm_last_error?: string | null;
  tsa_last_error?: string | null;
  /** Le dossier de preuve existe. Il se construit APRES la fin de
   *  l'enveloppe (25/09/2026) : « terminee » ne veut pas dire « dossier pret ». */
  proof_ready?: boolean;
  /** Ce qui est REELLEMENT programme apres un echec (25/09/2026) :
   *  - "nouvelle_tentative" : la signature sera retentee ou reportee ;
   *  - "reprise_automatique" : terminee sans dossier, reprise chaque heure ;
   *  - null : rien - tentatives epuisees, l'equipe technique est alertee. */
  reprise_programmee?: "nouvelle_tentative" | "reprise_automatique" | null;
  /** Enveloppe non signee (refus, annulation, peremption, echec technique) :
   *  son dossier de preuve (08/10/2026). null : enveloppe sans ce dossier. */
  dossier_non_signee?: "pret" | "en_preparation" | null;
}

export interface EvenementAudit {
  occurred_at: string;
  event_name: string;
  actor_type: string;
  actor_label?: string | null;
  payload?: Record<string, unknown> | null;
  /** Qui, en clair (14/10/2026). */
  acteur?: string | null;
  /** Le champ complete, la piece deposee, le destinataire (14/10/2026). */
  detail?: string | null;
}

export interface Facture {
  id: string;
  number: string;
  status_id: number;
  status_code: string;
  issued_at: string;
  due_at: string;
  paid_at?: string | null;
  currency_code: string;
  amount_ht_minor: number;
  vat_amount_minor: number;
  amount_ttc_minor: number;
  vat_rate_bp: number;
  vat_regime_code: string;
}

export interface DemandeRecharge {
  id: string;
  reference: string;
  /** Credit qui sera ajoute (hors taxes). */
  amount_minor: number;
  vat_rate_bp?: number;
  vat_amount_minor?: number;
  /** Montant REELLEMENT a virer. Different du credit des que la TVA
   *  s'applique - c'est celui-la qu'il faut afficher en grand. */
  amount_ttc_minor?: number;
  currency_code: string;
  status_id: number;
  status_code?: string;
  created_at?: string;
  settled_at?: string | null;
  valid_until?: string | null;
  iban?: string | null;
  bic?: string | null;
  beneficiaire?: string | null;
}

export interface Membre {
  user_id: string;
  email: string;
  role: string;
  full_name?: string | null;
  status?: string | null;
}

export interface CleApi {
  id: string;
  name: string;
  scopes: string[];
  created_at: string;
  revoked_at?: string | null;
  /** Presente UNIQUEMENT a la creation, jamais relisible ensuite. */
  key?: string;
}

export interface Webhook {
  id: string;
  name: string;
  target_url: string;
  subscribed_events: string[];
  active?: boolean;
  secret?: string;
}

// ------------------------------------------------------------- appels

/** T-32 : le choix du client (null = il suit la plateforme), la valeur de
 *  la plateforme, la valeur effective et les bornes fournies par le serveur. */
export interface ParametresClient {
  signing_link_days: number | null;
  defaut_plateforme: number;
  effectif: number;
  /** B-07 : ordre des signataires impose par defaut (vrai tant que le
   *  client n'a rien choisi). */
  signing_order_imposed: boolean;
  /** Message propose a chaque destinataire (porteur, 12/10/2026). */
  message_defaut: string | null;
  /** 15/10/2026 : signature avancée par clé d'accès ouverte pour ce client (Admin). */
  avancee_cle_acces?: boolean;
  /** Lot 2bis : réglages par défaut du compte (propriétaire ou administrateur). */
  transmission_defaut?: Reglage;
  correction_nom_defaut?: Reglage;
  /** Lot 2quater (19/10/2026) : e-mail des avis et des erreurs (enveloppes
   *  créées par l'API ; propriétaire ou administrateur). */
  email_avis: string[];
  /** Lot 2quater : valeurs par défaut des nouvelles enveloppes, app et API. */
  auth_method_defaut: "otp_email" | "otp_sms";
  langue_defaut: "fr" | "en";
  remplissage_ordre_impose: boolean;
  bornes: { signing_link_days: { min: number; max: number } };
}

/** Ce qu'un PUT /tenants/me/parametres peut changer (chaque champ facultatif). */
export type ParametresModifiables = Partial<Pick<ParametresClient,
  "signing_order_imposed" | "email_avis" | "auth_method_defaut" | "langue_defaut" | "remplissage_ordre_impose">>;

/** Cadre tel qu'il part au serveur : fraction de page, origine en haut a
 *  gauche - la meme grandeur que celle affichee a l'ecran. */
export interface ChampEnvoye {
  kind: "signature" | "date" | "nom" | "email" | "texte" | "mention" | "case"
    | "paraphe" | "radio" | "texte_fixe" | "heure" | "date_heure" | "liste" | "piece_jointe" | "date_saisie";
  /** Lot 3bis : consigne (texte), phrase a recopier (mention), intitule (case). */
  libelle?: string | null;
  obligatoire?: boolean;
  /** Bouton radio : les options d'un meme groupe s'excluent. */
  groupe?: string | null;
  /** Liste deroulante : les choix proposes. */
  options?: string[] | null;
  page_no: number;
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface FicheContact {
  email: string;
  given_name: string | null;
  family_name: string | null;
  organisation: string | null;
  phone_e164: string | null;
  auth_method: "otp_email" | "otp_sms";
}

export interface Contact {
  id: string;
  email: string;
  full_name: string | null;
  given_name: string | null;
  family_name: string | null;
  organisation: string | null;
  phone_e164: string | null;
  auth_method: "otp_email" | "otp_sms";
}

export type ReglePages = "premiere" | "derniere" | "absolue" | "refuser";

export interface Modele {
  id: string;
  name: string;
  page_rule: ReglePages;
  source_page_count: number;
  signer_count: number;
  field_count: number;
  created_at: string;
}

export const api = {
  /** tenantSlug : seulement si l'adresse est membre de plusieurs comptes (08/10/2026). */
  login: (email: string, password: string, tenantSlug?: string, totp?: string) =>
    request<{ access_token: string }>("/auth/login", {
      method: "POST",
      anonyme: true,
      body: { email, password, tenant_slug: tenantSlug || null, totp_code: totp || null },
    }),
  creerMonCompte: (email: string, password: string, totp?: string) =>
    request<{ access_token: string; numero: number; nom: string }>("/auth/creer-mon-compte", {
      method: "POST", anonyme: true, body: { email, password, totp_code: totp || null },
    }),
  ficheClient: () => request<FicheClient>("/tenants/me/fiche"),
  enregistrerFicheClient: (f: FicheClient) => {
    const { complete: _complete, ...corps } = f;
    return request<FicheClient>("/tenants/me/fiche", { method: "PUT", body: corps });
  },
  creerMonComptePersonnel: () =>
    request<{ access_token: string; numero: number; nom: string }>("/tenants/me/compte-personnel", { method: "POST" }),
  changerDeCompte: (numero: number) =>
    request<{ access_token: string }>("/auth/changer-compte", { method: "POST", body: { numero } }),

  /** Seul l'e-mail est requis cote serveur. Le slug est transmis quand on
   *  l'a, jamais exige : celui qui accede pour la premiere fois ne le
   *  connait pas encore. */
  demanderReinitialisation: (email: string, tenantSlug?: string) =>
    request<void>("/auth/password-reset/request", {
      method: "POST",
      anonyme: true,
      body: { email, ...(tenantSlug ? { tenant_slug: tenantSlug } : {}) },
    }),

  // Activation de la double authentification avant toute session (07/10/2026) :
  // le serveur exige l'e-mail et le mot de passe (comme dans l'Admin).
  mfaSetup: (email: string, password: string) =>
    request<{ provisioning_uri: string }>("/auth/mfa/setup", { method: "POST", anonyme: true, body: { email, password } }),
  mfaConfirm: (email: string, code: string) =>
    request<void>("/auth/mfa/confirm", { method: "POST", anonyme: true, body: { email, code } }),

  moi: () => request<Moi>("/users/me"),
  /** Proprietaire ou administrateur du compte (07/10/2026). */
  renommerCompte: (nom: string) => request<{ nom: string }>("/tenants/me/nom", { method: "PUT", body: { nom } }),
  produits: () => request<{ products: Produit[] } | Produit[]>("/products"),
  solde: () => request<Solde>("/credit/balance"),

  enveloppes: (limit = 20, offset = 0, filtres: Record<string, string> = {}) => {
    const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
    for (const [k, v] of Object.entries(filtres)) if (v) p.set(k, v);
    return request<{ total: number; envelopes: EnveloppeResume[] }>(`/envelopes?${p.toString()}`);
  },
  enveloppe: (id: string) => request<EnveloppeDetail>(`/envelopes/${id}`),
  auditEnveloppe: (id: string) => request<{ events: EvenementAudit[] }>(`/envelopes/${id}/audit`),
  annulerEnveloppe: (id: string, reason: string) =>
    request<void>(`/envelopes/${id}/cancel`, { method: "POST", body: { reason } }),
  relancerSignataire: (envelopeId: string, signerId: string) =>
    request<Invitation>(`/envelopes/${envelopeId}/signers/${signerId}/resend`, { method: "POST" }),
  passerEnLta: (id: string) => request<unknown>(`/envelopes/${id}/upgrade-to-lta`, { method: "POST" }),

  /** Modeles de placement (B-08 lot 3.3). La regle de pages est appliquee
   *  PAR LE SERVEUR : deux mises en oeuvre de la meme regle finiraient par
   *  diverger, et l'ecart ne se verrait qu'une fois l'enveloppe envoyee. */
  modeles: () => request<{ modeles: Modele[] }>("/modeles"),
  creerModele: (corps: {
    name: string;
    page_rule: ReglePages;
    source_page_count: number;
    signer_count: number;
    champs: (ChampEnvoye & { signer_index: number })[];
  }) => request<{ modele_id: string }>("/modeles", { method: "POST", body: corps }),
  champsDeModele: (id: string, pages: number, signataires: number) =>
    request<{ champs: (ChampEnvoye & { signer_index: number })[] }>(
      `/modeles/${id}/champs?pages=${pages}&signataires=${signataires}`),

  // « Securiser » (06/10/2026).
  devisScellement: (mode: ModeScellement) =>
    request<{ total_minor: number; currency_code: string }>(`/scellements/devis?mode=${mode}`),
  sceller: (documentId: string, mode: ModeScellement) =>
    request<{ id: string; etat: EtatScellement }>("/scellements", { method: "POST", body: { document_id: documentId, mode } }),
  scellement: (id: string) => request<{ id: string; etat: EtatScellement; erreur: string | null }>(`/scellements/${id}`),
  scellements: () => request<{ scellements: Scellement[] }>("/scellements"),
  /** Pieces jointes deposees par les signataires (12/10/2026). */
  champsEnveloppe: (id: string) =>
    request<{ champs: (ChampEnvoye & { id: string; signer_id: string; valeur: string | null })[] }>(
      `/envelopes/${id}/champs`),
  pieceJointe: (id: string, champId: string) =>
    request<Blob>(`/envelopes/${id}/champs/${champId}/fichier`, { binaire: true }),
  dossierNonSignee: (id: string) => request<Blob>(`/envelopes/${id}/dossier-non-signee`, { binaire: true }),
  documentScelle: (id: string) => request<Blob>(`/scellements/${id}/document`, { binaire: true }),
  dossierScellement: (id: string) => request<Blob>(`/scellements/${id}/dossier`, { binaire: true }),
  // « Prouver », bouton E-mail (06/10/2026).
  devisPreuveEnvoi: (mode: ModePreuve) =>
    request<{ total_minor: number; currency_code: string; taille_max_octets: number }>(`/preuves-envoi/devis?mode=${mode}`),
  envoyerAvecPreuve: (champs: { destinataire: string; objet: string; corps: string; nom_expediteur: string;
                                expediteur_id?: string; mode: ModePreuve },
                      fichiers: File[]) => {
    const form = new FormData();
    Object.entries(champs).forEach(([cle, valeur]) => { if (valeur) form.append(cle, valeur); });
    fichiers.forEach((f) => form.append("fichiers", f));
    return request<{ id: string }>("/preuves-envoi", { method: "POST", body: form });
  },
  preuvesEnvoi: () => request<{ preuves: PreuveEnvoi[] }>("/preuves-envoi"),
  preuveEnvoi: (id: string) => request<PreuveEnvoi>(`/preuves-envoi/${id}`),
  journalEnvoi: (id: string) => request<Blob>(`/preuves-envoi/${id}/journal`, { binaire: true }),
  expediteurs: () => request<Expediteurs>("/preuves-envoi/expediteurs"),
  ajouterExpediteur: (adresse: string) =>
    request<Expediteurs>("/preuves-envoi/expediteurs", { method: "POST", body: { adresse } }),
  verifierDomaine: (id: string) =>
    request<DomaineExpediteur>(`/preuves-envoi/expediteurs/domaines/${id}/verifier`, { method: "POST" }),
  supprimerExpediteur: (id: string) => request<void>(`/preuves-envoi/expediteurs/${id}`, { method: "DELETE" }),
  conservationProuver: () => request<ConservationProuver>("/preuves-envoi/conservation"),
  choisirConservation: (prolongationAuto: boolean) =>
    request<ConservationProuver>("/preuves-envoi/conservation", {
      method: "PUT", body: { prolongation_auto: prolongationAuto },
    }),
  modelesMessage: () => request<{ modeles: ModeleMessage[] }>("/preuves-envoi/modeles"),
  /** Un nom deja pris remplace le modele de ce nom. */
  enregistrerModele: (m: Omit<ModeleMessage, "id">) =>
    request<ModeleMessage>("/preuves-envoi/modeles", { method: "PUT", body: m }),
  supprimerModele: (id: string) => request<void>(`/preuves-envoi/modeles/${id}`, { method: "DELETE" }),
  creerDocument: (title: string) =>
    request<{ document_id: string }>("/documents", { method: "POST", body: { title } }),
  /** Contenu du PDF depose, tel que le SERVEUR l'a stocke (B-08 lot 3.1).
   *  On ne reaffiche pas le fichier local : le client doit voir ce qui
   *  sera reellement prepare et signe, et l'ecran du detail d'une
   *  enveloppe n'a de toute facon aucun fichier local sous la main. */
  contenuDocument: (documentId: string) =>
    request<Blob>(`/documents/${documentId}/current-version/content`, { binaire: true }),

  televerserVersion: (documentId: string, fichier: File) => {
    const form = new FormData();
    form.append("file", fichier);
    return request<{ id: string; hash_hex: string; byte_size: number; signatures_existantes?: number;
      converti?: string | null; signatures_numeriques_retirees?: number; contenu_actif_retire?: string[] }>(
      `/documents/${documentId}/versions`, { method: "POST", body: form },
    );
  },
  /** B-31 : plusieurs documents deposes, reunis en un seul PDF a signer.
   *  `parties` dit ou commence chaque document dans ce PDF. */
  fusionnerDocuments: (title: string, documentVersionIds: string[]) =>
    request<{ document_id: string; version: { id: string };
      parties: { document_version_id: string; filename: string; premiere_page: number; nb_pages: number }[] }>(
      "/documents/fusion", { method: "POST", body: { title, document_version_ids: documentVersionIds } }),

  /** Montant d'une enveloppe (B-08 lot 4) : le devis du serveur, meme
   *  calcul que le debit a la creation. Le CDC impose que le prix soit a
   *  l'ecran AVANT validation ; indisponible -> null plutot qu'un montant
   *  faux. */
  devis: async (productId: number, signataires: number): Promise<Devis | null> => {
    try {
      return await request<Devis>(`/envelopes/devis?product_id=${productId}&signataires=${signataires}`);
    } catch {
      return null;
    }
  },

  /** Annuaire (12/10/2026) : recherche a la frappe, 2 caracteres au moins. */
  contacts: (q: string) =>
    request<{ contacts: Contact[] }>(`/contacts?q=${encodeURIComponent(q)}`),
  // Gestionnaire de contacts (porteur, 13/10/2026).
  tousLesContacts: (q = "") =>
    request<{ contacts: Contact[] }>(`/contacts/tous${q.trim() ? `?q=${encodeURIComponent(q.trim())}` : ""}`),
  creerContact: (fiche: FicheContact) => request<Contact>("/contacts", { method: "POST", body: fiche }),
  modifierContact: (id: string, fiche: FicheContact) =>
    request<Contact>(`/contacts/${id}`, { method: "PUT", body: fiche }),
  supprimerContact: (id: string) => request<void>(`/contacts/${id}`, { method: "DELETE" }),

  creerEnveloppe: (corps: {
    title: string;
    product_id: number;
    document_version_ids: string[];
    signers: {
      email: string;
      full_name?: string;
      auth_method: string;
      phone_e164?: string;
      /** Message pour ce signataire, en plus du message commun. */
      message?: string;
      /** Annuaire : enregistrer ce signataire dans les contacts. */
      enregistrer_contact?: boolean;
      /** Lot 2bis : transmission de la signature (absent = reglage du compte). */
      transmission?: Reglage;
      given_name?: string;
      family_name?: string;
      organisation?: string;
      /** Langue du parcours et des e-mails de ce signataire. */
      langue?: string;
      /** B-13 : « approver » approuve sans signer. */
      role?: "signer" | "approver";
      // B-08 lot 3.2 : cadres poses sur les pages du client, en fraction de
      // page. Facultatifs - sans eux le document est signe pareil.
      champs?: ChampEnvoye[];
    }[];
    langue?: string;
    signing_order_imposed?: boolean;
    message?: string;
    copy_emails?: string[];
    /** Traitement avant signature (12/10/2026) : vide = pas de traitement. */
    intervenants?: IntervenantEnvoye[];
    remplissage_ordre_impose?: boolean;
  }) => request<{ envelope_id: string }>("/envelopes", { method: "POST", body: corps }),

  /** Point 9 du 02/10/2026 : `v0Sha256` = empreinte de la V0 que le
   *  concepteur a vue ; le serveur refuse d'envoyer un autre document. */
  envoyerEnveloppe: (id: string, v0Sha256?: string) =>
    request<{ signers: Invitation[]; intervenants?: { email: string; email_sent: boolean }[] }>(
      `/envelopes/${id}/send${v0Sha256 ? `?v0_sha256=${v0Sha256}` : ""}`, { method: "POST" }),
  /** Le PDF EXACT qui partira (V0), avant « Envoyer ce document ». */
  apercuV0: (id: string) => request<Blob>(`/envelopes/${id}/v0`, { binaire: true }),
  dupliquer: (id: string) => request<Duplication>(`/envelopes/${id}/duplication`, { method: "POST" }),
  relancerIntervenant: (envelopeId: string, intervenantId: string) =>
    request<{ email_sent: boolean }>(`/envelopes/${envelopeId}/intervenants/${intervenantId}/relance`, { method: "POST" }),

  aTraiter: () => request<{ a_traiter: TacheATraiter[] }>("/traitements"),
  traitement: (id: string) => request<Traitement>(`/traitements/${id}`),
  documentATraiter: (id: string) => request<Blob>(`/traitements/${id}/document`, { binaire: true }),
  saisirATraiter: (id: string, champId: string, valeur: string | null) =>
    request<void>(`/traitements/${id}/champs/${champId}`, { method: "PUT", body: { valeur } }),
  terminerATraiter: (id: string) => request<{ statut: string }>(`/traitements/${id}/terminer`, { method: "POST" }),
  validerATraiter: (id: string, accepte: boolean, motif?: string) =>
    request<{ statut: string }>(`/traitements/${id}/valider`, { method: "POST", body: { accepte, motif: motif ?? null } }),

  factures: () => request<{ total: number; invoices: Facture[] }>("/billing/invoices"),
  facturePdf: (id: string) => request<Blob>(`/billing/invoices/${id}/pdf`, { binaire: true }),
  recharges: () => request<{ top_ups: DemandeRecharge[] }>("/billing/top-ups"),
  /** Le PDF du BON DE COMMANDE : c'est ce document que le client
   *  transmet a sa comptabilite pour faire passer le virement. */
  bonDeCommandePdf: (id: string) =>
    request<Blob>(`/billing/top-ups/${id}/pdf`, { binaire: true }),
  demanderRecharge: (amountMinor: number) =>
    request<DemandeRecharge>("/billing/top-ups", {
      method: "POST",
      body: { amount_minor: amountMinor, currency_code: "EUR" },
    }),

  // T-32 : duree de vie du lien de signature (null = valeur de la plateforme).
  parametresClient: () => request<ParametresClient>("/tenants/me/parametres"),
  definirDureeLienClient: (jours: number | null) =>
    request<ParametresClient>("/tenants/me/parametres", { method: "PUT", body: { signing_link_days: jours } }),
  definirMessageDefaut: (message: string) =>
    request<ParametresClient>("/tenants/me/parametres", { method: "PUT", body: { message_defaut: message } }),
  definirReglageClient: (reglage: "transmission_defaut" | "correction_nom_defaut", valeur: Reglage) =>
    request<ParametresClient>("/tenants/me/parametres", { method: "PUT", body: { [reglage]: valeur } }),
  demandesEnAttente: () => request<{ demandes: DemandeSignataire[] }>("/envelopes/demandes-en-attente"),
  deciderDemande: (envelopeId: string, demandeId: string, accepte: boolean) =>
    request<{ id: string; statut: string }>(`/envelopes/${envelopeId}/demandes/${demandeId}`,
      { method: "POST", body: { accepte } }),
  definirParametres: (valeurs: ParametresModifiables) =>
    request<ParametresClient>("/tenants/me/parametres", { method: "PUT", body: valeurs }),

  membres: () => request<{ members: Membre[] } | Membre[]>("/tenants/me/members"),
  inviter: (email: string, role: string) =>
    request<unknown>("/tenants/me/invitations", { method: "POST", body: { email, role } }),

  clesApi: () => request<{ api_keys: CleApi[] } | CleApi[]>("/api-keys"),
  creerCleApi: (name: string, scopes: string[]) =>
    request<CleApi>("/api-keys", { method: "POST", body: { name, scopes } }),
  revoquerCleApi: (id: string) => request<void>(`/api-keys/${id}`, { method: "DELETE" }),

  webhooks: () => request<{ endpoints: Webhook[] } | Webhook[]>("/webhooks"),
  creerWebhook: (name: string, targetUrl: string, events: string[]) =>
    request<Webhook>("/webhooks", {
      method: "POST",
      body: { name, target_url: targetUrl, subscribed_events: events },
    }),
  supprimerWebhook: (id: string) => request<void>(`/webhooks/${id}`, { method: "DELETE" }),
  livraisonsWebhook: (id: string) => request<unknown>(`/webhooks/${id}/deliveries`),
};

/** Les listes de l'API sont tantot un tableau nu, tantot enveloppees dans
 *  un objet. On absorbe la difference ici plutot que dans chaque ecran. */
export function liste<T>(reponse: unknown, cle: string): T[] {
  if (Array.isArray(reponse)) return reponse as T[];
  const enveloppe = (reponse ?? {}) as Record<string, unknown>;
  const valeur = enveloppe[cle];
  return Array.isArray(valeur) ? (valeur as T[]) : [];
}
